"use client";

import { useEffect, useState } from "react";
import { CheckCircle } from "lucide-react";
import { SiteNav, SiteFooter } from "@/components/landing/chrome";
import { STYLES as BASE_STYLES } from "@/components/landing/LandingV2";
import { DISCOVER_FAQS } from "./faq-data";

const DISCOVER_STYLES = `
.lvlup-landing .dc-page { padding: 80px 0 96px; }
@media (max-width: 720px) { .lvlup-landing .dc-page { padding: 56px 0 64px; } }

.lvlup-landing .dc-hero { text-align: center; max-width: 760px; margin: 0 auto 56px; }
.lvlup-landing .dc-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  background: var(--accent-soft); border: 1px solid var(--accent-line);
  color: var(--accent);
  font-family: var(--mono); font-size: 11px; letter-spacing: 0.5px;
  text-transform: uppercase; font-weight: 600;
  padding: 8px 14px; border-radius: 999px;
  margin-bottom: 22px;
}
.lvlup-landing .dc-hero h1 {
  font-family: var(--display);
  font-size: clamp(38px, 5.4vw, 60px);
  font-weight: 700; letter-spacing: -2px; line-height: 1;
  margin: 0 0 20px; color: var(--ink); text-wrap: balance;
}
.lvlup-landing .dc-hero h1 .lp-accent { color: var(--accent); }
.lvlup-landing .dc-hero p { font-size: 17px; color: var(--ink-dim); margin: 0; text-wrap: pretty; }

.lvlup-landing .dc-grid {
  display: grid; grid-template-columns: 0.95fr 1.05fr; gap: 32px;
  align-items: start;
}
@media (max-width: 980px) { .lvlup-landing .dc-grid { grid-template-columns: 1fr; gap: 24px; } }

.lvlup-landing .dc-steps {
  display: flex; flex-direction: column; gap: 16px;
  margin-top: 8px;
}
.lvlup-landing .dc-step {
  background: var(--bg-raised); border: 1px solid var(--line);
  border-radius: 16px; padding: 22px 24px;
  display: flex; gap: 16px; align-items: flex-start;
}
.lvlup-landing .dc-step-num {
  flex-shrink: 0;
  width: 36px; height: 36px; border-radius: 10px;
  background: var(--accent); color: var(--accent-ink);
  font-family: var(--display); font-weight: 700; font-size: 15px;
  display: flex; align-items: center; justify-content: center;
  letter-spacing: -0.2px;
}
.lvlup-landing .dc-step h3 {
  font-family: var(--display); font-size: 16px; font-weight: 600;
  letter-spacing: -0.2px; margin: 4px 0 6px; color: var(--ink);
}
.lvlup-landing .dc-step p { font-size: 14px; color: var(--ink-dim); margin: 0; line-height: 1.55; text-wrap: pretty; }

.lvlup-landing .dc-learn {
  background: var(--bg-raised);
  border: 1px solid var(--accent-line);
  background-image: linear-gradient(180deg, oklch(0.88 0.18 120 / 0.06), transparent 60%);
  border-radius: 16px;
  padding: 24px;
  margin-top: 8px;
}
.lvlup-landing .dc-learn h4 {
  font-family: var(--mono); font-size: 11px; font-weight: 600;
  letter-spacing: 0.5px; text-transform: uppercase;
  color: var(--accent); margin: 0 0 14px;
}
.lvlup-landing .dc-learn ul {
  list-style: none; padding: 0; margin: 0;
  display: flex; flex-direction: column; gap: 10px;
}
.lvlup-landing .dc-learn li {
  display: flex; align-items: flex-start; gap: 10px;
  font-size: 14px; color: var(--ink); line-height: 1.5;
}
.lvlup-landing .dc-learn li svg { color: var(--accent); flex-shrink: 0; margin-top: 3px; }

.lvlup-landing .dc-booking {
  background: var(--bg-raised); border: 1px solid var(--line);
  border-radius: 20px;
  padding: 28px;
  position: relative;
  overflow: hidden;
}
@media (max-width: 720px) { .lvlup-landing .dc-booking { padding: 20px; border-radius: 16px; } }
.lvlup-landing .dc-booking h3 {
  font-family: var(--display); font-size: 22px; font-weight: 700;
  letter-spacing: -0.5px; margin: 0 0 8px; color: var(--ink);
  text-align: center;
}
.lvlup-landing .dc-booking p {
  font-size: 14px; color: var(--ink-dim); margin: 0 0 18px;
  text-align: center; line-height: 1.55;
}
.lvlup-landing .dc-booking-iframe-wrap {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--line);
}
.lvlup-landing .dc-booking-iframe-wrap iframe {
  display: block; width: 100%; min-height: 900px; border: 0;
}
`;

export default function DiscoverClient() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://get.lvlup-app.com/js/form_embed.js";
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="lvlup-landing">
      <style dangerouslySetInnerHTML={{ __html: BASE_STYLES + DISCOVER_STYLES }} />
      <SiteNav />

      <main>
        <section className="dc-page">
          <div className="lp-container">
            <div className="dc-hero">
              <span className="dc-eyebrow">Discover LVLUP</span>
              <h1>
                Transform your <span className="lp-accent">coaching business</span>
              </h1>
              <p>
                Book a personalized discovery call to explore how LVLUP can help you scale your business
                and deliver exceptional results to your clients.
              </p>
            </div>

            <div className="dc-grid">
              {/* Left — what you get */}
              <div>
                <div className="dc-steps">
                  <div className="dc-step">
                    <div className="dc-step-num">1</div>
                    <div>
                      <h3>Personalized demo</h3>
                      <p>
                        See LVLUP in action with a customized demo tailored to your specific coaching needs
                        and business goals.
                      </p>
                    </div>
                  </div>

                  <div className="dc-step">
                    <div className="dc-step-num">2</div>
                    <div>
                      <h3>Strategy session</h3>
                      <p>
                        Discuss your current challenges and discover how LVLUP&apos;s features can solve your
                        specific business problems.
                      </p>
                    </div>
                  </div>

                  <div className="dc-step">
                    <div className="dc-step-num">3</div>
                    <div>
                      <h3>Custom roadmap</h3>
                      <p>
                        Get a personalized implementation plan to maximize your success with LVLUP from
                        day one.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="dc-learn">
                  <h4>What you&apos;ll learn</h4>
                  <ul>
                    <li>
                      <CheckCircle size={16} />
                      <span>How to create your branded mobile app in 20 days</span>
                    </li>
                    <li>
                      <CheckCircle size={16} />
                      <span>Strategies to eliminate commission fees and keep 100% of sales</span>
                    </li>
                    <li>
                      <CheckCircle size={16} />
                      <span>Advanced features for scaling your coaching business</span>
                    </li>
                    <li>
                      <CheckCircle size={16} />
                      <span>Best practices from successful coaches using LVLUP</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right — booking widget */}
              <div className="dc-booking">
                <h3>Book your discovery call</h3>
                <p>Pick a time that works for you — free, no commitment.</p>
                <div className="dc-booking-iframe-wrap">
                  {mounted && (
                    <iframe
                      src="https://get.lvlup-app.com/widget/booking/TRinz5WYe6l9l5UvGF11"
                      id="booking-widget"
                      title="Book Discovery Call"
                      scrolling="no"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What to expect — FAQ (below the booking widget, above the footer).
            Same lp-faq design system as the homepage FAQ; Q&A text comes from
            faq-data.ts, the single source shared with the JSON-LD in page.tsx. */}
        <section className="lp-section">
          <div className="lp-container">
            <div className="lp-faq-grid">
              <div>
                <span className="lp-eyebrow">FAQ</span>
                <h2 className="lp-h2 lp-h2-left">
                  What to <span className="lp-accent">expect</span>
                </h2>
              </div>
              <div className="lp-faq-list">
                {DISCOVER_FAQS.map((faq, i) => (
                  <details className="lp-faq" key={faq.q} open={i === 0}>
                    <summary>{faq.q}</summary>
                    <p>{faq.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
